import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Button,
  Divider,
  Form,
  Grid,
  Header,
  Message,
  Segment,
  TextArea,
  Popup,
  Icon,
} from "semantic-ui-react";
import { useMediaQuery } from "react-responsive";
import { toast } from "react-toastify";
import { Formik, Form as FormikForm } from "formik";
import * as Yup from "yup";
import ModalWrapper from "../../../app/common/modals/modalWrapper";
import { closeModal } from "../../../app/common/modals/modalSlice";
import MyCheckbox from "../../../app/common/form/MyCheckbox";
import {
  partyIsBuyerAgent,
  partyIsListingAgent,
  isPartyCoAgentUser,
  isPartyTcUser,
  isPartyAssistantUser,
  buildAgentCoAgentContextLines,
} from "../../../app/common/util/util";
import {
  sendDocSharingEmail,
  addHistoryToDb,
  updateDocSentForSigningInDb,
  updatePartyInDb,
} from "../../../app/firestore/firestoreService";
import _ from "lodash";

// Helper function to update sharedEmailClients when agent sends for signing
async function updateSharedEmailClientsForAgentSending(
  clientsToSend,
  otherSideClientParties
) {
  console.log("=== updateSharedEmailClientsForAgentSending START ===");
  console.log("Clients to send:", JSON.stringify(clientsToSend, null, 2));
  console.log(
    "Other side client parties:",
    JSON.stringify(
      otherSideClientParties.map((p) => ({
        id: p.id,
        role: p.role,
        email: p.email,
        firstName: p.firstName,
        lastName: p.lastName,
        sharedEmailClients: p.sharedEmailClients,
      })),
      null,
      2
    )
  );

  // Group clients by email
  const emailGroups = {};
  clientsToSend.forEach((client) => {
    if (client.email) {
      if (!emailGroups[client.email]) {
        emailGroups[client.email] = [];
      }
      emailGroups[client.email].push(client);
    }
  });

  console.log("Email groups:", emailGroups);

  // For each email group with multiple clients, update sharedEmailClients
  for (const [email, clients] of Object.entries(emailGroups)) {
    if (clients.length <= 1) continue; // Skip if only one client uses this email

    console.log(
      `Processing email group: ${email} with ${clients.length} clients`
    );

    // Find the primary client (Buyer or Seller)
    const primaryClient = clients.find(
      (c) => c.role === "Buyer" || c.role === "Seller"
    );

    if (!primaryClient) {
      console.log("No primary client found for email:", email);
      continue;
    }

    console.log("Primary client:", primaryClient);

    // Find the primary party in the database
    const primaryParty = otherSideClientParties.find(
      (p) => p.role === primaryClient.role
    );

    if (!primaryParty) {
      console.log(
        "Primary party not found in database for role:",
        primaryClient.role
      );
      continue;
    }

    console.log("Primary party from DB:", primaryParty);

    // Build sharedEmailClients array
    const sharedEmailClients = [];

    // Get existing sharedEmailClients or initialize empty array
    const existingSharedClients = primaryParty.sharedEmailClients || [];

    // Process each client and update secondary parties
    for (const client of clients) {
      const isSecondaryOrTertiary =
        client.role === "Buyer 2" ||
        client.role === "Buyer 3" ||
        client.role === "Seller 2" ||
        client.role === "Seller 3";

      if (isSecondaryOrTertiary) {
        // Find the party in the database
        const secondaryParty = otherSideClientParties.find(
          (p) => p.role === client.role
        );

        if (secondaryParty) {
          console.log(`Adding ${client.role} to sharedEmailClients`);

          // Check if already exists in the array
          const alreadyExists = existingSharedClients.some(
            (sc) => sc.odPartyId === secondaryParty.id
          );

          if (!alreadyExists) {
            sharedEmailClients.push({
              role: client.role,
              odPartyId: secondaryParty.id,
            });
          } else {
            // Keep existing entry
            const existingEntry = existingSharedClients.find(
              (sc) => sc.odPartyId === secondaryParty.id
            );
            sharedEmailClients.push(existingEntry);
          }

          // Update the secondary party's email to match primary
          console.log(`Updating ${client.role} email to ${email}`);
          await updatePartyInDb(secondaryParty, { email: email });
        }
      }
    }

    // Merge with existing entries that aren't in the current email group
    existingSharedClients.forEach((existingEntry) => {
      const isInCurrentGroup = sharedEmailClients.some(
        (sc) => sc.odPartyId === existingEntry.odPartyId
      );
      if (!isInCurrentGroup) {
        sharedEmailClients.push(existingEntry);
      }
    });

    console.log(
      "Final sharedEmailClients for primary party:",
      sharedEmailClients
    );

    // Update primary party with sharedEmailClients 
    console.log(
      `Updating primary party ${primaryParty.id} (${primaryParty.role}) with:`,
      {
        sharedEmailClients,
        email: email,
      }
    );

    await updatePartyInDb(primaryParty, {
      sharedEmailClients,
      email: email, // Ensure primary party has the correct email
    });

    console.log(
      `✅ Primary party ${primaryParty.role} updated successfully in database`
    );
  }

  console.log("=== updateSharedEmailClientsForAgentSending COMPLETE ===");
}

export default function AgentSendForSigning({
  doc,
  party,
  transaction,
  allParties,
}) {
  const dispatch = useDispatch();
  const isMobile = useMediaQuery({ query: "(max-width:768px)" });
  const [selectedClients, setSelectedClients] = useState([]);
  const [note, setNote] = useState("");
  const [processing, setProcessing] = useState(false);
  const { currentUser } = useSelector((state) => state.auth);
  const { currentUserProfile } = useSelector((state) => state.profile);

  // Co-agents, TCs, and assistants share the same party portal link as the
  // agent — figure out who's actually logged in so the signing-request email
  // gets signed by them, not the agent on record.
  const loggedInEmail = currentUser?.email || currentUserProfile?.email;
  const isCoAgentUser = isPartyCoAgentUser(party, loggedInEmail);
  const isTcUser = isPartyTcUser(party, loggedInEmail);
  const isAssistantUser = isPartyAssistantUser(party, loggedInEmail);
  const actualSender = isCoAgentUser
    ? { ...party.coAgent, isTester: party.isTester }
    : isTcUser
    ? { ...party.transactionCoordinator, isTester: party.isTester }
    : isAssistantUser
    ? { ...party.assistant, isTester: party.isTester }
    : party;
  const agentContextLines =
    isCoAgentUser || isTcUser || isAssistantUser
      ? buildAgentCoAgentContextLines(party)
      : undefined;
  // Role to log in the client's "Documents Shared" history when it's not the
  // agent themselves sending.
  const actualSenderRole = isCoAgentUser
    ? "CoAgent"
    : isTcUser
    ? "TC"
    : isAssistantUser
    ? "Assistant"
    : null;

  // Get other-side client parties from transaction parties only
  const otherSideClientParties = useMemo(() => {
    if (!allParties || !transaction.agentRepresents) {
      return [];
    }

    let otherSideRoles = [];
    if (transaction.agentRepresents === "Buyer") {
      otherSideRoles = ["Seller", "Seller 2", "Seller 3"];
    } else if (transaction.agentRepresents === "Seller") {
      otherSideRoles = ["Buyer", "Buyer 2", "Buyer 3"];
    }

    return allParties.filter((p) => otherSideRoles.includes(p.role));
  }, [allParties, transaction.agentRepresents]);

  // Filter annotations for relevant signerRoles and signature/date types - memoized
  // Check both annotsInProgressSuggested (from form templates) and annotsInProgress (from duplicated/uploaded docs with manual annotations)
  const relevantAnnots = useMemo(() => {
    const allAnnots = [
      ...(doc.annotsInProgressSuggested || []),
      ...(doc.annotsInProgress || []),
    ];

    return allAnnots.filter((annot) => {
      // Include signature and date type annotations
      if (annot.type !== "signature" && annot.type !== "date") {
        return false;
      }

      if (
        partyIsBuyerAgent(party.role) &&
        annot.signerRole?.startsWith("Buyer")
      ) {
        return true;
      }
      if (
        partyIsListingAgent(party.role) &&
        annot.signerRole?.startsWith("Seller")
      ) {
        return true;
      }
      return false;
    });
  }, [doc.annotsInProgressSuggested, doc.annotsInProgress, party.role]);

  // Get only signature annotations for client selection (dates are auto-signed with signatures)
  const signatureAnnots = useMemo(() => {
    return relevantAnnots.filter((annot) => annot.type === "signature");
  }, [relevantAnnots]);

  // Function to check if a client has already signed
  const isClientSigned = useCallback(
    (clientRole) => {
      if (!doc.signingDetails) return false;
      const roleKey = _.camelCase(clientRole); // Convert "Seller 2" to "seller2"
      const signingDetail = doc.signingDetails[roleKey];
      return signingDetail && signingDetail.signed === true;
    },
    [doc.signingDetails]
  );

  // Create initial client list with auto-populated names (only for clients that exist in transaction parties)
  useEffect(() => {
    // Only include signature annotations that have corresponding parties in the transaction
    const filteredSignatureAnnots = signatureAnnots.filter((annot) =>
      otherSideClientParties.some((p) => p.role === annot.signerRole)
    );

    // Get unique roles from annotations (deduplicate by signerRole)
    const uniqueRoles = [
      ...new Set(filteredSignatureAnnots.map((annot) => annot.signerRole)),
    ];

    const initialClients = uniqueRoles.map((role) => {
      const existingParty = otherSideClientParties.find((p) => p.role === role);
      const fullName =
        existingParty?.firstName && existingParty?.lastName
          ? `${existingParty.firstName} ${existingParty.lastName}`
          : "";
      const hasSigned = isClientSigned(role);
      return {
        id: existingParty?.id || "",
        role: role,
        fullName: fullName,
        email: existingParty?.email || "",
        isFromTransaction: true, // All parties from transaction parties are considered "from transaction" (names uneditable)
        selected: !hasSigned, // Don't select clients who have already signed
        hasSigned: hasSigned,
      };
    });
    setSelectedClients(initialClients);
  }, [signatureAnnots, otherSideClientParties, isClientSigned]);

  function handleToggleClient(index) {
    const client = selectedClients[index];
    // Don't allow toggling for clients who have already signed
    if (client.hasSigned) {
      return;
    }
    const updatedClients = [...selectedClients];
    updatedClients[index].selected = !updatedClients[index].selected;
    setSelectedClients(updatedClients);
  }
  function handleClientChange(index, field, value) {
    const updatedClients = [...selectedClients];
    // Force email to lowercase
    if (field === "email") {
      value = value.toLowerCase().trim();
    }
    updatedClients[index][field] = value;
    setSelectedClients(updatedClients);
  }

  async function handleSendForSigning() {
    setProcessing(true);
    try {
      const clientsToSend = selectedClients.filter((client) => client.selected);

      if (clientsToSend.length === 0) {
        toast.error("Please select at least one client to send for signing.");
        setProcessing(false);
        return;
      }

      // Validate all selected clients have required fields
      const invalidClients = clientsToSend.filter(
        (client) => !client.fullName || !client.email
      );

      if (invalidClients?.length > 0) {
        toast.error("Please fill in all required fields for selected clients.");
        setProcessing(false);
        return;
      }

      const invalidClientEmails = clientsToSend.filter(
        (client) =>
          !client.email ||
          !/^[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+)*@[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?)*\.[a-zA-Z]{2,}$/.test(
            client.email
          )
      );

      if (invalidClientEmails?.length > 0) {
        toast.error(
          "Please correct the email address: this should only be a single valid email address for each client."
        );
        setProcessing(false);
        return;
      }

      // Validate email uniqueness with exception for primary/secondary/tertiary clients
      // Allow Buyer to share email with Buyer 2/3, and Seller to share email with Seller 2/3
      const emailGroups = {};
      clientsToSend.forEach((client) => {
        if (client.email) {
          if (!emailGroups[client.email]) {
            emailGroups[client.email] = [];
          }
          emailGroups[client.email].push(client.role);
        }
      });

      // Check for invalid email sharing
      const invalidEmailSharing = Object.entries(emailGroups).some(
        ([, roles]) => {
          if (roles.length === 1) return false; // Single use is always valid

          // Check if all roles sharing this email are from the same "family"
          // (Buyer, Buyer 2, Buyer 3) or (Seller, Seller 2, Seller 3)
          const buyerRoles = roles.filter((r) => r.startsWith("Buyer"));
          const sellerRoles = roles.filter((r) => r.startsWith("Seller"));

          // Valid if all are buyers OR all are sellers
          const allBuyers = buyerRoles.length === roles.length;
          const allSellers = sellerRoles.length === roles.length;

          if (!allBuyers && !allSellers) {
            return true; // Invalid: mixing buyer and seller emails
          }

          // Check if sharing is only between primary and secondary/tertiary
          const hasPrimary =
            roles.includes("Buyer") || roles.includes("Seller");
          const hasOnlyValidSecondary =
            buyerRoles.every(
              (r) => r === "Buyer" || r === "Buyer 2" || r === "Buyer 3"
            ) ||
            sellerRoles.every(
              (r) => r === "Seller" || r === "Seller 2" || r === "Seller 3"
            );

          // Invalid if no primary, or if roles are not valid combinations
          return !hasPrimary || !hasOnlyValidSecondary;
        }
      );

      if (invalidEmailSharing) {
        toast.error(
          "Email addresses can only be shared between primary client (Buyer/Seller) and their secondary/tertiary clients (Buyer 2/3 or Seller 2/3)."
        );
        setProcessing(false);
        return;
      }

      // Update document status and mark as sent by agent
      const docUpdateFields = {
        sentForSigningByAgent: true,
        sentByAgentId: party.id,
        sentByAgentAt: new Date(),
      };

      // Add agent marker to signingRequestedFor and split fullName
      // Only include fields needed for Firestore - exclude UI-only fields (selected, hasSigned, isFromTransaction)
      const updatedClientsToSend = clientsToSend.map((client) => {
        const nameParts = client.fullName.trim().split(" ");
        const firstName = nameParts[0] || "";
        const lastName = nameParts.slice(1).join(" ") || "";

        return {
          id: client.id,
          role: client.role,
          fullName: client.fullName,
          email: client.email,
          firstName,
          lastName,
          sentByAgent: true,
          agentId: party.id,
        };
      });

      // First, we need to move the relevant annotations from annotsInProgressSuggested to annotsInProgress
      // so that updateDocSentForSigningInDb can process them correctly
      const relevantAnnotsForSigning = relevantAnnots.filter((annot) =>
        updatedClientsToSend.some((client) => client.role === annot.signerRole)
      );

      // Create a copy of the document with the annotations moved to annotsInProgress
      const docWithAnnotsInProgress = {
        ...doc,
        annotsInProgress: relevantAnnotsForSigning,
      };

      await updateDocSentForSigningInDb(
        docWithAnnotsInProgress,
        note,
        updatedClientsToSend,
        transaction,
        docUpdateFields
      );

      // Update sharedEmailClients for parties that share emails
      await updateSharedEmailClientsForAgentSending(
        updatedClientsToSend,
        otherSideClientParties
      );

      // Send emails to clients. restrictedToPartyId keeps this out of the main
      // transaction's Email History — it's this agent's own team sending to
      // their own clients, not visible to the other side. actualSender signs
      // the email as whoever actually sent it (agent/CoAgent/TC/assistant);
      // agentContextLines adds the agent/CoAgent-on-record info when it's not
      // the agent themselves. This also logs the exact content sent to Email History.
      await sendDocSharingEmail(
        updatedClientsToSend,
        "signing",
        actualSender, // whoever is actually logged in and sending
        transaction,
        doc,
        party.id,
        agentContextLines
      );

      // Add history entries. Log the actual sender (agent/CoAgent/TC/assistant),
      // not always the agent on record, so "Documents Shared" shows who really did it.
      updatedClientsToSend.forEach((client) => {
        addHistoryToDb(
          transaction.id,
          actualSender,
          "sent for signing by agent",
          doc.name,
          client,
          actualSenderRole,
          party.id
        );
      });

      setProcessing(false);
      toast.success(
        "Document successfully sent for signing to selected clients"
      );
      dispatch(closeModal({ modalType: "AgentSendForSigning" }));
    } catch (error) {
      toast.error(error.message);
      setProcessing(false);
    }
  }

  function handleCancel() {
    dispatch(closeModal({ modalType: "AgentSendForSigning" }));
  }

  return (
    <ModalWrapper size="small">
      <Segment>
        <Grid>
          <Grid.Column width={16} className="zero bottom padding">
            <Header as="h2" color="blue" className="zero bottom margin">
              Send for Signing
            </Header>
            <Divider className="zero bottom margin" />
            <p>
              Select your clients to send this document for signing.
              <br />
              Add or update the email address for your clients.
              <br />
              <i>
                The name is what the TransActioner agent specified in the
                transaction.
                <br />
                If the name is incorrect, notify the other agent to correct it.
              </i>
              {!isMobile && (
                <Popup
                  flowing
                  size="small"
                  trigger={
                    <Icon
                      name="info"
                      color="blue"
                      circular
                      inverted
                      size="small"
                      style={{ marginLeft: "3px", marginBottom: "3px" }}
                    />
                  }
                >
                  <p className="bold text blue mini bottom margin">
                    How Your Clients Can Sign
                  </p>
                  <p className="bold text blue mini bottom margin"></p>
                  <div className="text-dark text-normal mini bottom margin">
                    <p>
                      Your clients will receive an email from transactioner.com
                      <br />
                      with a blue button link labeled "Sign Documents".
                      <br />
                      When they click on the button, they will be
                      <br />
                      prompted to log in with their email that you specify here
                      <br />
                      (same process you went through).
                      <br />
                      If they have not logged in before,
                      <br />
                      it will prompt them to create a password.
                      <br />
                      <i>
                        You can also send them the link:
                        https://app.transactioner.com/partyLogin
                      </i>
                    </p>
                    <p>
                      Once logged in, they will see a similar Client Portal{" "}
                      <br />
                      that you see, with the documents they need to sign
                      <br />
                      at the top in bright orange and a button labeled "Sign".
                    </p>
                    <p>
                      <i>
                        Note: The other agent will not be able to see your
                        clients' email addresses.
                      </i>
                    </p>
                    <p>
                      <i>
                        Questions? Contact support: support@transactioner.com
                      </i>
                    </p>
                  </div>
                </Popup>
              )}
            </p>
          </Grid.Column>

          <Grid.Column width={16}>
            {selectedClients.length === 0 ? (
              <Message warning>
                <Message.Header>No Signing Fields Found</Message.Header>
                <p>
                  This document doesn't have any signing fields for your
                  clients.
                </p>
              </Message>
            ) : (
              <Formik
                initialValues={{}}
                validationSchema={Yup.object({})}
                onSubmit={() => {}}
              >
                {() => (
                  <FormikForm>
                    {!isMobile && (
                      <Grid>
                        <Grid.Row>
                          <Grid.Column width={1}></Grid.Column>
                          <Grid.Column width={3}>Role</Grid.Column>
                          <Grid.Column width={5}>Name</Grid.Column>
                          <Grid.Column width={7}>Email</Grid.Column>
                        </Grid.Row>
                      </Grid>
                    )}
                    {selectedClients.map((client, index) => (
                      <Segment key={index} style={{ marginBottom: "10px" }}>
                        {isMobile ? (
                          // Mobile layout: checkbox and role on one line, name and email on separate lines
                          <Grid>
                            <Grid.Row>
                              <Grid.Column width={2}>
                                {client.hasSigned ? (
                                  <span
                                    style={{
                                      color: "green",
                                      fontWeight: "bold",
                                      fontSize: "12px",
                                    }}
                                  >
                                    Signed
                                  </span>
                                ) : (
                                  <MyCheckbox
                                    name={`selected_${index}`}
                                    checked={client.selected}
                                    onChange={() => handleToggleClient(index)}
                                  />
                                )}
                              </Grid.Column>
                              <Grid.Column width={14}>
                                <Header as="h4" style={{ margin: 0 }}>
                                  {client.role}
                                </Header>
                              </Grid.Column>
                            </Grid.Row>
                            <Grid.Row>
                              <Grid.Column width={16}>
                                <input
                                  type="text"
                                  placeholder="Full Name"
                                  value={client.fullName}
                                  onChange={(e) =>
                                    handleClientChange(
                                      index,
                                      "fullName",
                                      e.target.value
                                    )
                                  }
                                  disabled={client.isFromTransaction}
                                  style={{
                                    width: "100%",
                                    padding: "8px",
                                    border: "1px solid rgba(34,36,38,.15)",
                                    borderRadius: "4px",
                                    fontSize: "14px",
                                    backgroundColor: client.isFromTransaction
                                      ? "#f9f9f9"
                                      : "white",
                                    cursor: client.isFromTransaction
                                      ? "not-allowed"
                                      : "text",
                                  }}
                                />
                              </Grid.Column>
                            </Grid.Row>
                            <Grid.Row>
                              <Grid.Column width={16}>
                                <input
                                  type="email"
                                  placeholder="Email"
                                  value={client.email}
                                  onChange={(e) =>
                                    handleClientChange(
                                      index,
                                      "email",
                                      e.target.value
                                    )
                                  }
                                  disabled={client.hasSigned}
                                  style={{
                                    width: "100%",
                                    padding: "8px",
                                    border: "1px solid rgba(34,36,38,.15)",
                                    borderRadius: "4px",
                                    fontSize: "14px",
                                    backgroundColor: client.hasSigned
                                      ? "#f9f9f9"
                                      : "white",
                                    cursor: client.hasSigned
                                      ? "not-allowed"
                                      : "text",
                                  }}
                                />
                              </Grid.Column>
                            </Grid.Row>
                          </Grid>
                        ) : (
                          // Desktop layout: checkbox, role, name, and email in one line
                          <Grid>
                            <Grid.Row>
                              <Grid.Column width={1}>
                                {client.hasSigned ? (
                                  <span
                                    style={{
                                      color: "green",
                                      fontWeight: "bold",
                                      fontSize: "12px",
                                    }}
                                  >
                                    Signed
                                  </span>
                                ) : (
                                  <MyCheckbox
                                    name={`selected_${index}`}
                                    checked={client.selected}
                                    onChange={() => handleToggleClient(index)}
                                  />
                                )}
                              </Grid.Column>
                              <Grid.Column width={3}>
                                <Header as="h4" style={{ margin: 0 }}>
                                  {client.role}
                                </Header>
                              </Grid.Column>
                              <Grid.Column width={5}>
                                <input
                                  type="text"
                                  placeholder="Full Name"
                                  value={client.fullName}
                                  onChange={(e) =>
                                    handleClientChange(
                                      index,
                                      "fullName",
                                      e.target.value
                                    )
                                  }
                                  disabled={client.isFromTransaction}
                                  style={{
                                    height: "28px",
                                    minWidth: "140px",
                                    width: "100%",
                                    padding: "6px 8px",
                                    border: "1px solid rgba(34,36,38,.15)",
                                    borderRadius: "4px",
                                    fontSize: "14px",
                                    backgroundColor: client.isFromTransaction
                                      ? "#f9f9f9"
                                      : "white",
                                    cursor: client.isFromTransaction
                                      ? "not-allowed"
                                      : "text",
                                  }}
                                />
                              </Grid.Column>
                              <Grid.Column width={7}>
                                <input
                                  type="email"
                                  placeholder="Email"
                                  value={client.email}
                                  onChange={(e) =>
                                    handleClientChange(
                                      index,
                                      "email",
                                      e.target.value
                                    )
                                  }
                                  disabled={client.hasSigned}
                                  style={{
                                    height: "28px",
                                    minWidth: "220px",
                                    width: "100%",
                                    padding: "6px 8px",
                                    border: "1px solid rgba(34,36,38,.15)",
                                    borderRadius: "4px",
                                    fontSize: "14px",
                                    backgroundColor: client.hasSigned
                                      ? "#f9f9f9"
                                      : "white",
                                    cursor: client.hasSigned
                                      ? "not-allowed"
                                      : "text",
                                  }}
                                />
                              </Grid.Column>
                            </Grid.Row>
                          </Grid>
                        )}
                      </Segment>
                    ))}
                  </FormikForm>
                )}
              </Formik>
            )}
          </Grid.Column>

          <Grid.Column width={16}>
            <Header as="h3" color="blue">
              Add a note (optional)
            </Header>
            <Form>
              <TextArea
                rows={3}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Add a message for your clients..."
              />
            </Form>
          </Grid.Column>

          <Grid.Column width={16} className="zero top padding">
            <Divider className="zero top margin" />
            {processing ? (
              <Button
                primary
                loading
                floated={isMobile ? null : "right"}
                className={isMobile ? "fluid medium bottom margin" : null}
              >
                Sending...
              </Button>
            ) : (
              <Button
                floated={isMobile ? null : "right"}
                primary
                onClick={handleSendForSigning}
                // disabled={
                //   selectedClients.filter((c) => c.selected).length === 0
                // }
                className={isMobile ? "fluid medium bottom margin" : null}
              >
                Send for Signatures
              </Button>
            )}

            <Button
              floated={isMobile ? null : "right"}
              onClick={handleCancel}
              className={isMobile ? "fluid medium bottom margin" : null}
            >
              Cancel
            </Button>
          </Grid.Column>
        </Grid>
      </Segment>
    </ModalWrapper>
  );
}
